import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormProvider, Controller, useForm, useWatch } from 'react-hook-form-v7';
import type { Resolver } from 'react-hook-form-v7';
import { yupResolver } from '@hookform/resolvers/yup';
import { toast } from 'sonner';
import {
  Alert,
  AlertDescription,
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@parrot-software/design-system';
import { Info as InfoIcon, Loader2 } from '@parrot-software/design-system/icons';
import { cn } from '@parrot-software/utils';
import { logger } from '@parrot-software/observability';
import type { CreateInvoiceDataDto, InvoiceDataViewDto } from '@subscriptions/application/dtos';
import { isRetryInvoiceError } from '@subscriptions/application/errors';
import { extractErrorAttributes } from '@subscriptions/application/utils';
import type { RetryStatus } from '@subscriptions/presentation/constants';
import {
  InvoiceDataSources,
  RetryScopes,
  RetryStatuses,
  getCfdiUseForTaxRegime,
} from '@subscriptions/presentation/constants';
import {
  useCreateInvoiceData,
  useFiscalRegimes,
  useRetryInvoiceActions,
  useSubscriptionAnalytics,
  useUpdateInvoiceData,
} from '@subscriptions/presentation/hooks';
import type { CsfPrefillValues } from '@subscriptions/presentation/utils';
import { resolveInvoiceErrorMessages } from '@subscriptions/presentation/utils';
import {
  createInvoiceDataFormSchema,
  DEFAULT_CFDI_VALUE,
  getInvoicePersonType,
  INVOICE_DATA_MAX_LENGTHS,
  isPublicoGeneralRfc,
  mapViewDtoToFormValues,
  sanitizeBusinessName,
} from './InvoiceDataForm.schema';
import type { InvoiceDataFormValues, InvoicePersonType } from './InvoiceDataForm.schema';

const invoiceLog = logger.forDomain('SUB', { subdomain: 'billing' });

export type InvoiceDataFormMode = 'create' | 'edit' | 'retry';

/** Modes that load an existing record and persist through the update mutation */
const UPDATE_MODES: InvoiceDataFormMode[] = ['edit', 'retry'];

export interface InvoiceDataFormProps {
  fieldsClassName?: string;
  footerClassName?: string;
  footerStart?: React.ReactNode;
  /** Content rendered at the top of the scrollable fields area, before form fields */
  headerContent?: React.ReactNode;
  initialValues?: InvoiceDataViewDto;
  mode?: InvoiceDataFormMode;
  /** Block the retry belongs to; only used to attribute the retry outcome. */
  blockUuid?: string;
  onlyFailed?: boolean;
  paymentUuid?: string | null;
  prefillValues?: CsfPrefillValues;
  shouldReset?: boolean;
  onCancel: () => void;
  onDirtyChange?: (isDirty: boolean) => void;
  onSuccess?: (invoiceData: InvoiceDataViewDto) => Promise<void> | void;
}

/**
 * InvoiceDataForm renders the fiscal data creation fields and actions.
 *
 * This is a pure form component (no layout wrapper). It is designed to be
 * embedded inside any container (Sheet, Modal, Page, etc.).
 *
 * When `onDirtyChange` is provided, the parent is notified every time the
 * form's dirty state (`formState.isDirty`) changes. In the update modes (`edit`
 * and `retry`) the form is reset against `initialValues`, so the dirty flag
 * reflects real changes over the loaded fiscal data; `shouldReset` resets the
 * form (and the flag) when the hosting sheet closes.
 *
 * `retry` saves through the same update mutation as `edit`, then re-runs the failed payment
 * named by `paymentUuid` (the retry link's `?paymentUuid=` query param). `onSuccess` — and
 * with it the success toast and the sheet closing — fires only once that retry resolves, so
 * a failed retry never reads as a successful payment even though the save is committed.
 *
 * `footerStart` is an optional slot rendered as the first child of the footer
 * row, letting hosts (e.g. the block creation wizard) place an element — such
 * as the wizard back button — on the same row as the submit action.
 *
 * @param {InvoiceDataFormProps} props - Component properties
 * @returns {React.ReactElement} Invoice data form
 */
export const InvoiceDataForm = ({
  fieldsClassName,
  footerClassName,
  footerStart,
  headerContent,
  initialValues,
  mode = 'create',
  blockUuid,
  onlyFailed = false,
  paymentUuid,
  prefillValues,
  shouldReset,
  onCancel,
  onDirtyChange,
  onSuccess,
}: InvoiceDataFormProps): React.ReactElement => {
  const { t } = useTranslation('invoiceData');
  const { mutateAsync: createInvoiceData, isLoading: isCreating } = useCreateInvoiceData();
  const { mutateAsync: updateInvoiceData, isLoading: isUpdating } = useUpdateInvoiceData();
  const { mutateAsync: retryInvoiceData, isLoading: isRetrying } = useRetryInvoiceActions();
  const { data: fiscalRegimes } = useFiscalRegimes();
  const isUpdateMode = UPDATE_MODES.includes(mode);
  // The mutation flags drop to false between the save and the retry, and again while `onSuccess`
  // runs — which in retry mode holds the sheet open for a beat. `isLocked` spans the whole submit
  // so the button never blinks back to enabled and invites a second click mid-flight.
  const [isLocked, setIsLocked] = useState(false);
  const isSubmitting = isCreating || isUpdating || isRetrying || isLocked;
  const schema = useMemo(() => createInvoiceDataFormSchema(t), [t]);
  const resolver = useMemo(() => (yupResolver(schema) as unknown) as Resolver<InvoiceDataFormValues>, [schema]);

  const formMethods = useForm<InvoiceDataFormValues>({
    defaultValues: {
      businessName: '',
      firstName: '',
      firstLastName: '',
      secondLastName: '',
      postalCode: '',
      rfc: '',
      cfdi: DEFAULT_CFDI_VALUE,
      email: '',
      taxRegimeSatCode: '',
    },
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver,
  });

  const {
    control,
    formState: { errors, isDirty, isValid },
    handleSubmit,
    reset,
    resetField,
    setFocus,
    setValue,
    trigger,
  } = formMethods;

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const rfc = useWatch({ control, name: 'rfc' });
  const taxRegimeSatCode = useWatch({ control, name: 'taxRegimeSatCode' });
  // CFDI usage is not chosen by the user: it is derived from the selected fiscal
  // regime (see getCfdiUseForTaxRegime / CfdiUseByTaxRegime mapping).
  const resolvedCfdi = getCfdiUseForTaxRegime(taxRegimeSatCode ?? '');

  useEffect(() => {
    setValue('cfdi', resolvedCfdi, { shouldValidate: true });
  }, [resolvedCfdi, setValue]);
  const personType = getInvoicePersonType(rfc ?? '');
  // The "público general" RFC can't be invoiced: keep its RFC error visible but hide the
  // rest of the fields, since none of them apply to it.
  const isPublicoGeneral = isPublicoGeneralRfc(rfc ?? '');
  const previousPersonTypeRef = useRef<InvoicePersonType>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);
  /** Tracks the RFC from the last prefill to detect when a new CSF is uploaded. */
  const lastPrefillRfcRef = useRef<string | undefined>(undefined);

  const availableTaxRegimes = useMemo(() => {
    if (personType === 'company') return fiscalRegimes?.taxRegimeCompany ?? [];
    if (personType === 'person') return fiscalRegimes?.taxRegimePersons ?? [];
    return [];
  }, [fiscalRegimes, personType]);

  useEffect(() => {
    // Don't reset if prefill exists — the prefill effect handles setting all values including
    // the tax regime. This prevents the race condition where this effect runs before prefill's
    // reset() and clears the taxRegimeSatCode that prefill is about to set.
    if (prefillValues) {
      previousPersonTypeRef.current = personType;
      return;
    }

    if (previousPersonTypeRef.current !== null && previousPersonTypeRef.current !== personType) {
      resetField('taxRegimeSatCode', { defaultValue: '' });
    }
    previousPersonTypeRef.current = personType;
  }, [personType, prefillValues, resetField]);

  useEffect(() => {
    if (shouldReset) {
      reset();
    }
  }, [shouldReset, reset]);

  useEffect(() => {
    if (isUpdateMode && initialValues) {
      reset(mapViewDtoToFormValues(initialValues));
    }
  }, [isUpdateMode, initialValues, reset]);

  // Only empty fields are filled, so a regime resolved after the catalog loads still lands.
  // When a different CSF is uploaded (RFC changed), all CSF-derived fields are replaced
  // so the form reflects the new document. The email — never in the CSF — is focused so
  // the user lands on the only field left to fill.
  useEffect(() => {
    if (isUpdateMode || !prefillValues) return;

    const currentValues = formMethods.getValues();
    // First prefill: user started manually (or form was empty) and is now uploading a CSF.
    // All CSF-derived fields should be replaced since the user explicitly chose to use the document.
    const isFirstPrefill = lastPrefillRfcRef.current === undefined;
    const isNewCsf = !isFirstPrefill && lastPrefillRfcRef.current !== prefillValues.rfc;

    // Detect person type change to clear fields that no longer apply.
    // e.g., switching from persona física to persona moral should clear firstName/lastName fields.
    const previousPersonType = getInvoicePersonType(lastPrefillRfcRef.current ?? currentValues.rfc ?? '');
    const newPersonType = getInvoicePersonType(prefillValues.rfc ?? '');
    const isPersonTypeChange =
      previousPersonType !== null && newPersonType !== null && previousPersonType !== newPersonType;

    // Fields specific to each person type — cleared when switching to the other type.
    const personFields: (keyof InvoiceDataFormValues)[] = ['firstName', 'firstLastName', 'secondLastName'];
    const companyFields: (keyof InvoiceDataFormValues)[] = ['businessName'];

    // When switching person types (e.g., manual persona física → CSF persona moral),
    // replace all CSF-derived fields and clear fields from the old type.
    // When uploading a new CSF (same type), replace fields that have a resolved value.
    // Otherwise, only fill empty fields (preserves manual edits and allows regime to resolve).
    let nextValues = Object.entries(prefillValues).reduce(
      (values, [field, value]) => {
        const key = field as keyof InvoiceDataFormValues;
        // taxRegimeSatCode MUST be replaced when person type changes, even if the prefill value
        // is empty (e.g., fiscalRegimes not loaded yet). The old regime options are invalid for
        // the new person type, so clearing ensures the user selects from the correct list.
        // Also clear on first prefill since we're switching from manual to document mode.
        const mustClearOnTypeChange = key === 'taxRegimeSatCode' && (isPersonTypeChange || isFirstPrefill);
        // Replace field if:
        // - First prefill with value (user chose document over manual entry)
        // - taxRegimeSatCode on person type change or first prefill (clear old/invalid selection)
        // - Person type changed and new value exists (full replacement on type switch)
        // - New CSF (same type) with a resolved value (don't overwrite with empty strings)
        // - Or field is currently empty and new value exists
        const shouldReplace =
          mustClearOnTypeChange ||
          (isFirstPrefill && value) ||
          (isPersonTypeChange && value) ||
          (isNewCsf && value) ||
          (!values[key] && value);
        return shouldReplace ? { ...values, [key]: value } : values;
      },
      { ...currentValues },
    );

    // Clear fields from the previous person type when switching types.
    // Also clear on first prefill when we have a valid new person type, since manual entry may have
    // included fields that don't apply to the document's person type.
    if (isPersonTypeChange || (isFirstPrefill && newPersonType !== null)) {
      const fieldsToClear = newPersonType === 'company' ? personFields : companyFields;
      nextValues = fieldsToClear.reduce((values, field) => ({ ...values, [field]: '' }), nextValues);
    }

    lastPrefillRfcRef.current = prefillValues.rfc;

    reset(nextValues);
    void trigger();

    // Deferred via nested rAF: the first frame fires after React commits, and the second
    // waits for the paint that renders the email field (gated by personType computed from
    // the reset's RFC). setTimeout alone can fire before React 18's concurrent render.
    let frameId: number;
    const focusEmail = () => {
      setFocus('email');
      emailInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };
    frameId = requestAnimationFrame(() => {
      frameId = requestAnimationFrame(focusEmail);
    });

    return () => cancelAnimationFrame(frameId);
  }, [formMethods, isUpdateMode, prefillValues, reset, setFocus, trigger]);

  const submitLabels: Record<InvoiceDataFormMode, string> = {
    create: t('invoiceDataForm.submit'),
    edit: t('invoiceDataForm.update'),
    retry: t('invoiceDataForm.updateAndRetry'),
  };
  // While the request is in flight the button states what it is doing, so the spinner is not the
  // only signal — in retry mode especially, where the wait covers a save plus the re-invoicing.
  const submittingLabels: Record<InvoiceDataFormMode, string> = {
    create: t('invoiceDataForm.submitting'),
    edit: t('invoiceDataForm.updating'),
    retry: t('invoiceDataForm.retrying'),
  };
  const submitLabel = isSubmitting ? submittingLabels[mode] : submitLabels[mode];
  const isRetryMode = mode === 'retry';
  const { trackInvoiceDataUpdated, trackInvoiceRetryFinished } = useSubscriptionAnalytics();

  const handleSubmitForm = handleSubmit(async (values) => {
    const selectedTaxRegime = availableTaxRegimes.find((taxRegime) => taxRegime.satCode === values.taxRegimeSatCode);

    if (!selectedTaxRegime) return;

    const baseFields: CreateInvoiceDataDto = {
      businessName: personType === 'company' ? sanitizeBusinessName(values.businessName) : '',
      firstName: personType === 'person' ? values.firstName.trim().toUpperCase() : '',
      firstLastName: personType === 'person' ? values.firstLastName.trim().toUpperCase() : '',
      secondLastName: personType === 'person' ? values.secondLastName.trim().toUpperCase() : '',
      postalCode: values.postalCode,
      rfc: values.rfc.trim().toUpperCase(),
      cfdi: getCfdiUseForTaxRegime(selectedTaxRegime.satCode),
      email: values.email.trim(),
      taxRegime: selectedTaxRegime.satCode,
    };

    // Exactly one terminal event per retry, whichever branch gets there first.
    let hasReportedRetry = false;
    const reportRetryOutcome = (status: RetryStatus, rejectedCount: number) => {
      if (!isRetryMode || hasReportedRetry) return;
      hasReportedRetry = true;
      trackInvoiceRetryFinished({
        blockUuid,
        rejectedCount,
        scope: onlyFailed ? RetryScopes.FAILED_ONLY : RetryScopes.FULL_PAYMENT,
        status,
      });
    };

    setIsLocked(true);

    try {
      // `retry` persists through the same update request as `edit`, then runs its own actions.
      const result =
        isUpdateMode && initialValues?.uuid
          ? await updateInvoiceData({ uuid: initialValues.uuid, dto: baseFields, includeDeleted: isRetryMode })
          : await createInvoiceData(baseFields);

      if (isUpdateMode && initialValues) {
        const previous = initialValues;
        trackInvoiceDataUpdated({
          changedFields: (Object.keys(baseFields) as (keyof typeof baseFields)[]).filter(
            (field) => baseFields[field] !== previous[field],
          ),
          source: isRetryMode ? InvoiceDataSources.RETRY : InvoiceDataSources.EDIT,
        });
      }

      // In retry mode success belongs to the retry, not to the save: the fiscal data is only
      // the input the charge is re-run with. A retry that fails — or that has no payment to
      // re-run, because the link carried no `?paymentUuid=` — reports the error and leaves the
      // sheet open, so the user is never told the payment went through when it did not.
      // The save above is already committed either way.
      if (isRetryMode) {
        try {
          if (!paymentUuid) throw new Error('Missing paymentUuid');
          await retryInvoiceData({ includeDeleted: true, onlyFailed, paymentUuid });
        } catch (error) {
          // When the PAC rejected the CFDI the backend says exactly why, so the toast names the
          // field to fix instead of the generic copy — it is the whole point of coming here.
          const reasons = isRetryInvoiceError(error)
            ? resolveInvoiceErrorMessages(error.reasons.join(';'), t, baseFields.rfc)
            : [];
          // Reasons quote the PAC and can carry the taxpayer's RFC, so only their count travels.
          invoiceLog.error('submitRetryInvoiceForm', {
            'invoice.paymentUuid': paymentUuid ?? undefined,
            'invoice.onlyFailed': onlyFailed,
            'invoice.invoiceDataErrors': reasons.length,
            ...extractErrorAttributes(error),
          });
          toast.error(t('invoiceDataForm.retryError'), { description: reasons.join(' ') || undefined });
          reportRetryOutcome(
            isRetryInvoiceError(error) ? RetryStatuses.REJECTED : RetryStatuses.ERROR,
            isRetryInvoiceError(error) ? error.reasons.length : 0,
          );
          return;
        }

        // Outside the try above: a throw while reporting must not be read as the charge failing,
        // which would tell the user it did not go through after the CFDIs were already stamped.
        reportRetryOutcome(RetryStatuses.ISSUED, 0);
      }

      await onSuccess?.(result);
    } catch {
      toast.error(t('invoiceDataForm.unknownError'));
      // The save itself failed, so the retry never ran. Reported after the toast: what the user
      // is told must never depend on the tracking going through.
      reportRetryOutcome(RetryStatuses.ERROR, 0);
    } finally {
      // Released only once `onSuccess` settled: in retry mode that call holds the sheet open
      // while the backend works through the queued action, and the button must stay dead
      // for all of it. On the paths that leave the sheet open (a failed retry, a create whose
      // block assignment failed) this is what makes the form usable again.
      setIsLocked(false);
    }
  });

  const renderTextField = ({
    name,
    label,
    placeholder,
    className,
    disabled = false,
    inputRef,
    required = true,
    type = 'text',
    maxLength,
    transformValue,
    onBlur,
  }: {
    name: keyof InvoiceDataFormValues;
    label: string;
    placeholder: string;
    className?: string;
    disabled?: boolean;
    inputRef?: React.RefObject<HTMLInputElement>;
    required?: boolean;
    type?: 'text' | 'email';
    maxLength?: number;
    transformValue?: (value: string) => string;
    onBlur?: () => void;
  }) => (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <div className={cn('flex flex-col gap-1', className)}>
          <Label className="text-sm font-medium text-foreground" htmlFor={name}>
            {label} {required && <span className="text-destructive">*</span>}
          </Label>
          <Input
            {...field}
            className={cn({ 'border-destructive': errors[name], 'bg-muted text-foreground': disabled })}
            disabled={disabled}
            id={name}
            maxLength={maxLength}
            onBlur={() => {
              field.onBlur();
              onBlur?.();
            }}
            onChange={(event) => {
              const nextValue = transformValue ? transformValue(event.target.value) : event.target.value;
              field.onChange(nextValue);
            }}
            placeholder={placeholder}
            ref={(el) => {
              field.ref(el);
              if (inputRef) (inputRef as React.MutableRefObject<HTMLInputElement | null>).current = el;
            }}
            type={type}
            value={field.value ?? ''}
          />
          {errors[name]?.message && <p className="text-xs text-destructive">{errors[name]?.message}</p>}
        </div>
      )}
    />
  );

  return (
    <FormProvider {...formMethods}>
      <form className="flex min-h-0 flex-1 flex-col" onSubmit={handleSubmitForm}>
        <div className={cn('flex min-h-0 flex-1 flex-col gap-4', fieldsClassName)}>
          {headerContent}

          {prefillValues && (
            <Alert className="col-span-full flex items-start gap-2 border-info bg-bg-info text-info">
              <span className="shrink-0 pt-0.5">
                <InfoIcon size={16} />
              </span>
              <AlertDescription className="text-sm">{t('invoiceDataForm.csfHint')}</AlertDescription>
            </Alert>
          )}

          {renderTextField({
            name: 'rfc',
            label: t('invoiceDataForm.rfc'),
            maxLength: 13,
            placeholder: t('invoiceDataForm.placeholders.rfc'),
            transformValue: (value) => value.toUpperCase().replace(/\s/g, ''),
          })}

          {personType && !isPublicoGeneral && (
            <>
              {personType === 'company' &&
                renderTextField({
                  name: 'businessName',
                  label: t('invoiceDataForm.businessName'),
                  maxLength: INVOICE_DATA_MAX_LENGTHS.businessName,
                  placeholder: t('invoiceDataForm.placeholders.businessName'),
                  transformValue: (value) => value.toUpperCase(),
                })}

              {personType === 'person' && (
                <>
                  {renderTextField({
                    name: 'firstName',
                    label: t('invoiceDataForm.firstName'),
                    maxLength: INVOICE_DATA_MAX_LENGTHS.firstName,
                    placeholder: t('invoiceDataForm.placeholders.firstName'),
                    transformValue: (value) => value.toUpperCase(),
                  })}
                  {renderTextField({
                    name: 'firstLastName',
                    label: t('invoiceDataForm.firstLastName'),
                    maxLength: INVOICE_DATA_MAX_LENGTHS.firstLastName,
                    placeholder: t('invoiceDataForm.placeholders.firstLastName'),
                    transformValue: (value) => value.toUpperCase(),
                  })}
                  {renderTextField({
                    name: 'secondLastName',
                    label: t('invoiceDataForm.secondLastName'),
                    maxLength: INVOICE_DATA_MAX_LENGTHS.secondLastName,
                    placeholder: t('invoiceDataForm.placeholders.secondLastName'),
                    required: false,
                    transformValue: (value) => value.toUpperCase(),
                  })}
                </>
              )}

              <div className="flex flex-col gap-1">
                <Label className="text-sm font-medium text-foreground" htmlFor="personType">
                  {t('invoiceDataForm.personType')} <span className="text-destructive">*</span>
                </Label>
                <Input
                  className="bg-muted text-foreground"
                  disabled
                  id="personType"
                  value={
                    personType === 'company'
                      ? t('invoiceDataForm.personTypes.company')
                      : t('invoiceDataForm.personTypes.person')
                  }
                />
              </div>

              <div className="flex flex-col gap-1">
                <Label className="text-sm font-medium text-foreground" htmlFor="cfdi">
                  {t('invoiceDataForm.cfdi')} <span className="text-destructive">*</span>
                </Label>
                <Input
                  className="bg-muted text-foreground"
                  disabled
                  id="cfdi"
                  value={t(`cfdiValues.${resolvedCfdi}`)}
                />
              </div>

              {renderTextField({
                name: 'postalCode',
                label: t('invoiceDataForm.postalCode'),
                maxLength: 5,
                placeholder: t('invoiceDataForm.placeholders.postalCode'),
                transformValue: (value) => value.replace(/\D/g, '').slice(0, 5),
              })}

              <Controller
                control={control}
                name="taxRegimeSatCode"
                render={({ field }) => (
                  <div className="flex flex-col gap-1">
                    <Label className="text-sm font-medium text-foreground" htmlFor="taxRegimeSatCode">
                      {t('invoiceDataForm.taxRegime')} <span className="text-destructive">*</span>
                    </Label>
                    <Select
                      disabled={!availableTaxRegimes.length}
                      onValueChange={field.onChange}
                      value={field.value ?? ''}
                    >
                      <SelectTrigger
                        className={cn('[&>span]:flex-1 [&>span]:min-w-0 [&>span]:text-left', {
                          'border-destructive': errors.taxRegimeSatCode,
                        })}
                        id="taxRegimeSatCode"
                      >
                        <SelectValue placeholder={t('invoiceDataForm.placeholders.taxRegime')} />
                      </SelectTrigger>
                      <SelectContent className="z-1400 w-[385px]">
                        {availableTaxRegimes.map((taxRegime) => (
                          <SelectItem key={taxRegime.satCode} value={taxRegime.satCode}>
                            {`${taxRegime.satCode} - ${taxRegime.description}`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.taxRegimeSatCode?.message && (
                      <p className="text-xs text-destructive">{errors.taxRegimeSatCode.message}</p>
                    )}
                  </div>
                )}
              />

              {renderTextField({
                name: 'email',
                inputRef: emailInputRef,
                label: t('invoiceDataForm.email'),
                placeholder: t('invoiceDataForm.placeholders.email'),
                type: 'email',
              })}
            </>
          )}
        </div>

        <div className={cn('mt-4 flex flex-col gap-2', footerClassName)}>
          {footerStart}
          <Button className="w-full" disabled={!personType || !isValid || isSubmitting} type="submit">
            {submitLabel}
            {isSubmitting && <Loader2 className="ml-2 h-4 w-4 animate-spin" />}
          </Button>
          <Button className="w-full" onClick={onCancel} type="button" variant="outline">
            {t('invoiceDataForm.cancel')}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};
